import React, { useState, useEffect } from 'react';
import { Save, Clock, Calendar, Building2, Tag, Globe, Plus, Trash2, Edit2, RefreshCw, X } from 'lucide-react';
import { SlaConfigService, SlaConfig, PrioridadesService, DepartamentosService } from '../../services';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const DIAS_SEMANA = [
  { valor: 1, nome: 'Segunda-feira' },
  { valor: 2, nome: 'Terça-feira' },
  { valor: 3, nome: 'Quarta-feira' },
  { valor: 4, nome: 'Quinta-feira' },
  { valor: 5, nome: 'Sexta-feira' },
  { valor: 6, nome: 'Sábado' },
  { valor: 7, nome: 'Domingo' }
];

export default function SlaConfigPage() {
  const { usuario } = useAuth();
  const [configs, setConfigs] = useState<SlaConfig[]>([]);
  const [prioridades, setPrioridades] = useState<any[]>([]);
  const [departamentos, setDepartamentos] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<SlaConfig>>({
    horario_comercial_ativo: false,
    hora_inicio: '08:00',
    hora_fim: '18:00',
    dias_semana: [1, 2, 3, 4, 5],
    pausar_finais_semana: true,
    feriados: [],
    timezone: 'America/Sao_Paulo'
  });

  useEffect(() => {
    if (usuario && (usuario.nivel === 'admin' || usuario.nivel === 'dev')) {
      loadData();
    }
  }, [usuario]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [configsData, prioridadesData, departamentosData] = await Promise.all([
        SlaConfigService.listar(),
        PrioridadesService.getPrioridades(),
        DepartamentosService.getDepartamentos()
      ]);
      setConfigs(configsData);
      setPrioridades(prioridadesData);
      setDepartamentos(departamentosData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar configurações de SLA');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      
      const dataToSave: any = {
        ...formData,
        hora_inicio: formData.hora_inicio + ':00',
        hora_fim: formData.hora_fim + ':00',
        horario_comercial_ativo: formData.horario_comercial_ativo ? 1 : 0,
        pausar_finais_semana: formData.pausar_finais_semana ? 1 : 0,
        prioridade_id: formData.prioridade_id || null,
        departamento_id: formData.departamento_id || null
      };

      if (editingId) {
        await SlaConfigService.atualizar(editingId, dataToSave);
        toast.success('Configuração atualizada com sucesso!');
      } else {
        await SlaConfigService.criar(dataToSave);
        toast.success('Configuração criada com sucesso!');
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({
        horario_comercial_ativo: false,
        hora_inicio: '08:00',
        hora_fim: '18:00',
        dias_semana: [1, 2, 3, 4, 5],
        pausar_finais_semana: true,
        feriados: [],
        timezone: 'America/Sao_Paulo'
      });
      loadData();
    } catch (error: any) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar configuração');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (config: SlaConfig) => {
    setEditingId(config.id!);
    setFormData({
      ...config,
      hora_inicio: config.hora_inicio.substring(0, 5),
      hora_fim: config.hora_fim.substring(0, 5)
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Tem certeza que deseja deletar esta configuração?')) {
      return;
    }

    try {
      await SlaConfigService.deletar(id);
      toast.success('Configuração deletada com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao deletar configuração:', error);
      toast.error('Erro ao deletar configuração');
    }
  };

  const toggleDiaSemana = (dia: number) => {
    const dias = formData.dias_semana || [];
    if (dias.includes(dia)) {
      setFormData({ ...formData, dias_semana: dias.filter(d => d !== dia) });
    } else {
      setFormData({ ...formData, dias_semana: [...dias, dia] });
    }
  };

  const addFeriado = () => {
    const data = prompt('Digite a data do feriado (YYYY-MM-DD):');
    if (data && /^\d{4}-\d{2}-\d{2}$/.test(data)) {
      const feriados = formData.feriados || [];
      if (!feriados.includes(data)) {
        setFormData({ ...formData, feriados: [...feriados, data] });
      }
    } else if (data) {
      toast.error('Formato de data inválido. Use YYYY-MM-DD');
    }
  };

  const removeFeriado = (data: string) => {
    const feriados = formData.feriados || [];
    setFormData({ ...formData, feriados: feriados.filter(f => f !== data) });
  };

  const getConfigLabel = (config: SlaConfig) => {
    if (config.prioridade_id && config.departamento_id) {
      const prioridade = prioridades.find(p => p.id === config.prioridade_id);
      const departamento = departamentos.find(d => d.id === config.departamento_id);
      return `${prioridade?.nome || 'N/A'} + ${departamento?.nome || 'N/A'}`;
    } else if (config.prioridade_id) {
      const prioridade = prioridades.find(p => p.id === config.prioridade_id);
      return `Prioridade: ${prioridade?.nome || 'N/A'}`;
    } else if (config.departamento_id) {
      const departamento = departamentos.find(d => d.id === config.departamento_id);
      return `Departamento: ${departamento?.nome || 'N/A'}`;
    }
    return 'Configuração Global';
  };

  if (!usuario || (usuario.nivel !== 'admin' && usuario.nivel !== 'dev')) {
    return (
      <div className="space-y-6">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-red-200/50 dark:border-red-800/50 shadow-soft">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">Acesso negado. Apenas administradores podem acessar esta página.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Configurações de SLA
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Configure horário comercial e regras de SLA
            </p>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setFormData({
                horario_comercial_ativo: false,
                hora_inicio: '08:00',
                hora_fim: '18:00',
                dias_semana: [1, 2, 3, 4, 5],
                pausar_finais_semana: true,
                feriados: [],
                timezone: 'America/Sao_Paulo'
              });
            }}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nova Configuração
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingId ? 'Editar Configuração' : 'Nova Configuração'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="escopo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Escopo
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                    <select
                      id="escopo"
                      value={formData.prioridade_id || ''}
                      onChange={(e) => setFormData({ ...formData, prioridade_id: e.target.value ? parseInt(e.target.value) : null, departamento_id: null })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Global</option>
                      {prioridades.map(p => (
                        <option key={p.id} value={p.id}>{p.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {formData.prioridade_id && (
                  <div>
                    <label htmlFor="departamento" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Departamento (opcional)
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                      <select
                        id="departamento"
                        value={formData.departamento_id || ''}
                        onChange={(e) => setFormData({ ...formData, departamento_id: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Todos</option>
                        {departamentos.map(d => (
                          <option key={d.id} value={d.id}>{d.nome}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="horario_comercial_ativo"
                  checked={formData.horario_comercial_ativo}
                  onChange={(e) => setFormData({ ...formData, horario_comercial_ativo: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-400 dark:border-gray-600"
                />
                <label htmlFor="horario_comercial_ativo" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ativar horário comercial
                </label>
              </div>

              {formData.horario_comercial_ativo && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="hora_inicio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Hora de Início *
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                        <input
                          type="time"
                          id="hora_inicio"
                          value={formData.hora_inicio}
                          onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="hora_fim" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Hora de Fim *
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                        <input
                          type="time"
                          id="hora_fim"
                          value={formData.hora_fim}
                          onChange={(e) => setFormData({ ...formData, hora_fim: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Dias da Semana *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {DIAS_SEMANA.map(dia => (
                        <label key={dia.valor} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <input
                            type="checkbox"
                            checked={formData.dias_semana?.includes(dia.valor)}
                            onChange={() => toggleDiaSemana(dia.valor)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-400 dark:border-gray-600"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{dia.nome}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="pausar_finais_semana"
                      checked={formData.pausar_finais_semana}
                      onChange={(e) => setFormData({ ...formData, pausar_finais_semana: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-400 dark:border-gray-600"
                    />
                    <label htmlFor="pausar_finais_semana" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Pausar SLA em finais de semana
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Feriados
                    </label>
                    {formData.feriados && formData.feriados.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {formData.feriados.map(feriado => (
                          <span
                            key={feriado}
                            className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                          >
                            <Calendar size={14} />
                            {feriado}
                            <button
                              type="button"
                              onClick={() => removeFeriado(feriado)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <Trash2 size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={addFeriado}
                      className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                    >
                      <Plus size={16} />
                      Adicionar Feriado
                    </button>
                  </div>
                </>
              )}

              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Salvar Configuração
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista de Configurações */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
            Configurações Existentes
          </h3>
          
          {configs.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Nenhuma configuração de SLA encontrada. Crie uma nova configuração para começar.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map(config => (
                <div
                  key={config.id}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        {config.prioridade_id ? (
                          <Tag className="text-primary-600 dark:text-primary-400" size={18} />
                        ) : config.departamento_id ? (
                          <Building2 className="text-primary-600 dark:text-primary-400" size={18} />
                        ) : (
                          <Globe className="text-primary-600 dark:text-primary-400" size={18} />
                        )}
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {getConfigLabel(config)}
                        </h3>
                      </div>
                      {config.horario_comercial_ativo ? (
                        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2 ml-7">
                          <div className="flex items-center gap-2">
                            <Clock size={14} />
                            <span>Horário: {config.hora_inicio.substring(0, 5)} - {config.hora_fim.substring(0, 5)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar size={14} />
                            <span>Dias: {config.dias_semana.map(d => DIAS_SEMANA.find(ds => ds.valor === d)?.nome).join(', ')}</span>
                          </div>
                          {config.pausar_finais_semana && (
                            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                              <span>✓</span>
                              <span>Pausa em finais de semana</span>
                            </div>
                          )}
                          {config.feriados && config.feriados.length > 0 && (
                            <div className="flex items-center gap-2">
                              <Calendar size={14} />
                              <span>Feriados: {config.feriados.length}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 ml-7">
                          Horário comercial desativado
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(config)}
                        className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => config.id && handleDelete(config.id)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Deletar"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

